// Screen recording capture — #163 Task 5c PR A.
//
// Pure capture layer: feature detection, MediaRecorder lifecycle, codec
// selection, auto-stop at 5:00, microphone-audio-where-available. No UI,
// no consent modal, no AttachmentManager wiring — those land in PR B so
// the capture module can be reviewed and merged on its own.
//
// Browser matrix (per ticket):
//   - Chrome / Firefox / Edge desktop: WebM (vp8) full happy path
//   - Safari macOS ≥17: WebM/MP4 fallback path
//   - Safari iOS ≥17.4: same as macOS, audio warning when audio codec missing
//   - Safari iOS 17.0-17.3: no recording (UI hides Record button + banner)
//   - Mobile Chrome / Firefox iOS (WebKit): same as host Safari version
//
// Codec selection order:
//   1. video/webm; codecs=vp8     — Chromium + Firefox primary
//   2. video/mp4                  — Safari primary
//   Anything else → no_supported_codec, recording refused.
//
// Audio:
//   Microphone capture runs through getUserMedia({audio: true}) and the
//   audio track is added to the same MediaStream as the screen video. If
//   getUserMedia rejects (user denies or no mic), recording proceeds
//   video-only — better UX than a hard fail. The result includes an
//   `audioCaptured` flag so the caller can show a "no audio captured"
//   warning post-recording.
//
// Cleanup:
//   Every code path that creates a MediaStream is responsible for
//   `track.stop()` on each track when the controller terminates. Without
//   it, the browser keeps the screen-share / mic-active indicator on
//   indefinitely — a privacy bug + a "is OnCatch still recording me?"
//   trust hit.

export type RecordingCodec = 'video/webm; codecs=vp8' | 'video/mp4';

export type RecordingState = 'recording' | 'stopped' | 'error';

export interface CapabilityCheckResult {
  supported: boolean;
  /** When supported=false, the first capability that failed. */
  reason?:
    | 'no_get_display_media'
    | 'no_media_recorder'
    | 'no_supported_codec'
    // #485: the API exists but a host Permissions-Policy (`display-capture=()`)
    // disallows it, so getDisplayMedia would reject with NotAllowedError /
    // SecurityError on click. Detected up front so the button can hide/disable
    // instead of throwing when the reporter clicks it.
    | 'display_capture_blocked';
  /** When supported=true, the codec we'll pass to MediaRecorder. */
  codec?: RecordingCodec;
}

/**
 * #485: Permissions-Policy probe. A host page can restrict powerful features
 * via `Permissions-Policy: display-capture=()` / `camera=()` (or an
 * `<iframe>` that omits `allow="display-capture; camera"`). In that case the
 * `getDisplayMedia` / `getUserMedia` methods STILL exist on
 * navigator.mediaDevices — they simply reject with NotAllowedError /
 * SecurityError when invoked. Presence-checking the method is therefore not
 * enough; we must consult the policy up front so the capture buttons degrade
 * gracefully (hidden + inline notice) instead of throwing on click.
 *
 * `document.featurePolicy.allowsFeature(name)` is the introspection API for
 * this (Chromium; the Permissions-Policy spec's JS surface). It's feature-
 * detected: when the API is absent (Firefox/Safari today) we return `true`
 * — i.e. don't pre-hide on browsers that can't introspect; the click-time
 * error handling already surfaces a friendly message there. Wrapped in
 * try/catch because some engines throw on an unrecognized feature name.
 */
function permissionsPolicyAllows(feature: 'display-capture' | 'camera'): boolean {
  try {
    if (typeof document === 'undefined') return true;
    const policy = (
      document as Document & {
        featurePolicy?: { allowsFeature?: (f: string) => boolean };
      }
    ).featurePolicy;
    if (typeof policy?.allowsFeature === 'function') {
      return policy.allowsFeature(feature);
    }
  } catch {
    // Unknown feature name or introspection threw → fall through to permissive.
  }
  // API unavailable → don't pre-hide; let the click-time rejection path handle
  // it (it already shows a friendly "Recording unavailable" message).
  return true;
}

export interface RecordingResult {
  blob: Blob;
  filename: string;
  /**
   * Content-type of the produced blob, with codec params stripped. Always
   * one of `'video/webm'` or `'video/mp4'` — the runtime helper
   * `contentTypeFor()` strips the `; codecs=...` suffix off the codec
   * string the recorder was started with.
   */
  contentType: 'video/webm' | 'video/mp4';
  durationMs: number;
  /** True when the 5-minute auto-stop fired; false when the caller stopped manually. */
  autoStopped: boolean;
  /** True when a microphone audio track was successfully attached. False = video-only. */
  audioCaptured: boolean;
}

export interface RecordingController {
  /** Current state at the moment of access. Not reactive — subscribe via onStateChange. */
  readonly state: RecordingState;
  /** True after auto-stop or explicit stop() resolves. */
  readonly stopped: boolean;
  /** Stop the recording and resolve with the final result. */
  stop(): Promise<RecordingResult>;
  /** Cancel without producing a result. Tracks are stopped, no blob is emitted. */
  cancel(): void;
  /** Subscribe to state changes. Returns an unsubscribe function. */
  onStateChange(cb: (state: RecordingState, detail?: StateChangeDetail) => void): () => void;
}

export interface StateChangeDetail {
  durationMs?: number;
  error?: string;
  /** Set when state === 'stopped' to indicate auto-stop vs manual stop. */
  autoStopped?: boolean;
}

export interface StartRecordingOptions {
  /** Hard cap on recording duration. Default 5 minutes. */
  maxDurationMs?: number;
  /** Bitrate cap in bits/sec passed to MediaRecorder. Default 2.5 Mbps. */
  maxBitrate?: number;
  /** Try to capture microphone audio. Default true; falls back gracefully on rejection. */
  audio?: boolean;
  /** Pluggable for tests. Defaults to navigator.mediaDevices.getDisplayMedia. */
  getDisplayMedia?: (constraints?: MediaStreamConstraints) => Promise<MediaStream>;
  /** Pluggable for tests. Defaults to navigator.mediaDevices.getUserMedia. */
  getUserMedia?: (constraints: MediaStreamConstraints) => Promise<MediaStream>;
  /** Pluggable for tests. Defaults to global MediaRecorder constructor. */
  createRecorder?: (stream: MediaStream, options: MediaRecorderOptions) => MediaRecorderLike;
  /** Pluggable for tests. Defaults to MediaRecorder.isTypeSupported. */
  isTypeSupported?: (type: string) => boolean;
  /** Pluggable for tests. Defaults to global setTimeout. */
  setTimeout?: (cb: () => void, ms: number) => unknown;
  /** Pluggable for tests. Defaults to global clearTimeout. */
  clearTimeout?: (handle: unknown) => void;
  /** Pluggable for tests. Defaults to Date.now. */
  now?: () => number;
}

/**
 * Minimal interface that compressVideo + this module need from MediaRecorder.
 * Mirrors the shape of the global MediaRecorder so production code passes the
 * real constructor without adapter logic; tests can pass a vitest stub that
 * matches this surface without mocking the entire MediaRecorder DOM API.
 */
export interface MediaRecorderLike {
  start(timeslice?: number): void;
  stop(): void;
  ondataavailable: ((event: { data: Blob }) => void) | null;
  onstop: (() => void) | null;
  onerror: ((event: { error: Error }) => void) | null;
  state: 'inactive' | 'recording' | 'paused';
  mimeType?: string;
}

export const DEFAULT_MAX_DURATION_MS = 5 * 60 * 1000;
export const DEFAULT_MAX_BITRATE = 2_500_000;

const PREFERRED_CODECS: ReadonlyArray<RecordingCodec> = ['video/webm; codecs=vp8', 'video/mp4'];

/**
 * Check whether the current environment can record. Pure feature detection
 * — no permission prompts, no streams allocated. Safe to call on widget
 * mount to decide whether to render the Record button at all.
 */
export function checkRecordingSupport(
  options: { isTypeSupported?: (type: string) => boolean; ignorePermissionsPolicy?: boolean } = {},
): CapabilityCheckResult {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getDisplayMedia) {
    return { supported: false, reason: 'no_get_display_media' };
  }
  // #485: API exists, but a host Permissions-Policy may forbid it. If so,
  // getDisplayMedia would reject on click — report unsupported up front so the
  // button hides + the inline notice shows instead of throwing reactively.
  if (!options.ignorePermissionsPolicy && !permissionsPolicyAllows('display-capture')) {
    return { supported: false, reason: 'display_capture_blocked' };
  }
  if (typeof MediaRecorder === 'undefined') {
    return { supported: false, reason: 'no_media_recorder' };
  }
  const isTypeSupported =
    options.isTypeSupported ?? MediaRecorder.isTypeSupported.bind(MediaRecorder);
  for (const codec of PREFERRED_CODECS) {
    if (isTypeSupported(codec)) {
      return { supported: true, codec };
    }
  }
  return { supported: false, reason: 'no_supported_codec' };
}

/**
 * Start a screen recording. Returns a controller the caller can stop or
 * cancel; auto-stop fires at maxDurationMs without intervention.
 *
 * Throws if support check fails or if the user dismisses the screen-pick
 * dialog (getDisplayMedia rejects). Caller should surface "permission
 * denied" / "no display selected" to UI; this module doesn't classify
 * the error string beyond the underlying DOMException.
 */
export async function startRecording(
  options: StartRecordingOptions = {},
): Promise<RecordingController> {
  const isTypeSupported =
    options.isTypeSupported ?? MediaRecorder.isTypeSupported.bind(MediaRecorder);
  const support = checkRecordingSupport({ isTypeSupported });
  if (!support.supported || !support.codec) {
    throw new Error(`recording_unsupported:${support.reason ?? 'unknown'}`);
  }

  const maxDurationMs = options.maxDurationMs ?? DEFAULT_MAX_DURATION_MS;
  const maxBitrate = options.maxBitrate ?? DEFAULT_MAX_BITRATE;
  const wantAudio = options.audio !== false;

  const getDisplayMedia =
    options.getDisplayMedia ?? ((c) => navigator.mediaDevices.getDisplayMedia(c));
  const getUserMedia = options.getUserMedia ?? ((c) => navigator.mediaDevices.getUserMedia(c));
  const createRecorder =
    options.createRecorder ??
    ((stream, opts) => new MediaRecorder(stream, opts) as unknown as MediaRecorderLike);
  const setTimeoutFn = options.setTimeout ?? ((cb, ms) => setTimeout(cb, ms));
  const clearTimeoutFn = options.clearTimeout ?? ((handle) => clearTimeout(handle as number));
  const now = options.now ?? (() => Date.now());

  // Capture display first. User-facing screen-share dialog runs here; if the
  // user cancels we propagate the rejection to the caller before requesting
  // mic permission so we don't double-prompt for nothing.
  //
  // Picker hints to make Chrome's "Choose what to share" dialog less
  // confusing for non-technical reporters:
  //   - displaySurface: 'browser'  → opens the dialog with the Tab section
  //                                  active by default (vs. Window or Screen).
  //   - preferCurrentTab: true     → Chrome ≥ 105: pre-highlights the current
  //                                  tab so the user just clicks Share. Cast
  //                                  via `as any` because the lib.dom.d.ts
  //                                  type doesn't include the Chrome-only
  //                                  field; non-Chrome browsers ignore it.
  // The picker itself is a hard browser-security requirement — it cannot be
  // skipped. These hints just shorten the path to "click Share."
  // preferCurrentTab is a Chrome-only field not yet in lib.dom.d.ts; non-
  // Chrome browsers ignore it. Build the constraints with a Record<string,
  // unknown> view to add the extension field without an `as any` cast or
  // a lint suppression.
  const constraints: Record<string, unknown> = {
    video: { displaySurface: 'browser' },
    preferCurrentTab: true,
  };
  const displayStream = await getDisplayMedia(constraints as MediaStreamConstraints);

  // Audio is best-effort. A user might deny mic permission OR the platform
  // (e.g., Safari iOS) may not deliver mic audio reliably alongside display
  // capture. Fall back to video-only on any rejection.
  let audioCaptured = false;
  if (wantAudio) {
    try {
      const audioStream = await getUserMedia({ audio: true });
      for (const track of audioStream.getAudioTracks()) {
        displayStream.addTrack(track);
        audioCaptured = true;
      }
    } catch {
      // No mic — proceed video-only.
    }
  }

  const codec = support.codec;
  const recorder = createRecorder(displayStream, {
    mimeType: codec,
    videoBitsPerSecond: maxBitrate,
  });

  const chunks: Blob[] = [];
  const startedAt = now();
  let stopHandle: unknown;
  let internalState: RecordingState = 'recording';
  let autoStopped = false;
  let stopPromise: Promise<RecordingResult> | null = null;
  let stopResolve: ((r: RecordingResult) => void) | null = null;
  let stopReject: ((e: Error) => void) | null = null;
  // Captured the moment recorder.onstop / onerror fires, even if stop() was
  // never called yet (auto-stop, browser "Stop sharing", track ended). Lets
  // a later stop() return the cached result/error instead of hanging on a
  // resolver that will never fire again.
  let cachedResult: RecordingResult | null = null;
  let cachedError: Error | null = null;
  let suppressResult = false;
  const stateListeners = new Set<(s: RecordingState, d?: StateChangeDetail) => void>();

  function emit(state: RecordingState, detail?: StateChangeDetail): void {
    internalState = state;
    for (const cb of stateListeners) {
      try {
        cb(state, detail);
      } catch {
        // Listener errors must not break the recording lifecycle.
      }
    }
  }

  function stopAllTracks(): void {
    for (const track of displayStream.getTracks()) {
      try {
        track.stop();
      } catch {
        // Track may already be ended; ignore.
      }
    }
  }

  recorder.ondataavailable = (event) => {
    if (event.data && event.data.size > 0) chunks.push(event.data);
  };

  recorder.onstop = () => {
    if (stopHandle !== undefined) clearTimeoutFn(stopHandle);
    stopAllTracks();
    const durationMs = now() - startedAt;
    if (suppressResult) {
      // cancel() asked us to throw the chunks away.
      emit('stopped', { autoStopped: false });
      return;
    }
    const blob = new Blob(chunks, { type: codec });
    const result: RecordingResult = {
      blob,
      filename: filenameFor(codec, startedAt),
      contentType: contentTypeFor(codec),
      durationMs,
      autoStopped,
      audioCaptured,
    };
    cachedResult = result;
    emit('stopped', { durationMs, autoStopped });
    if (stopResolve) {
      stopResolve(result);
      stopResolve = null;
    }
  };

  recorder.onerror = (event) => {
    if (stopHandle !== undefined) clearTimeoutFn(stopHandle);
    stopAllTracks();
    const error = event.error ?? new Error('recorder_error');
    cachedError = error;
    emit('error', { error: error.message });
    if (stopReject) {
      stopReject(error);
      stopReject = null;
    }
  };

  // Browsers ALSO fire an inactive 'ended' event on the screen-share track
  // when the user clicks the browser's "Stop sharing" toolbar button.
  // Treat that as a manual stop so we still flush the recorder and resolve
  // with the captured chunks.
  for (const track of displayStream.getVideoTracks()) {
    track.addEventListener('ended', () => {
      if (recorder.state === 'recording') {
        try {
          recorder.stop();
        } catch {
          // Already stopped.
        }
      }
    });
  }

  recorder.start(1000); // 1s timeslice → ondataavailable fires periodically
  emit('recording');

  // Auto-stop after maxDurationMs. Marked autoStopped so the result
  // distinguishes the cap-hit case from manual stop.
  stopHandle = setTimeoutFn(() => {
    if (recorder.state === 'recording') {
      autoStopped = true;
      try {
        recorder.stop();
      } catch {
        // Already stopped.
      }
    }
  }, maxDurationMs);

  // Build the controller surface. stop()/cancel() guarantee idempotency:
  // calling stop twice returns the same promise; calling cancel after stop
  // is a no-op.
  const controller: RecordingController = {
    get state() {
      return internalState;
    },
    get stopped() {
      return internalState === 'stopped' || internalState === 'error';
    },
    stop() {
      if (stopPromise) return stopPromise;
      // Fast-path: recorder.onstop already fired (auto-stop, browser stop-
      // sharing, track ended). Return the cached result without queueing a
      // resolver that would never fire.
      if (cachedResult) return Promise.resolve(cachedResult);
      if (cachedError) return Promise.reject(cachedError);
      stopPromise = new Promise<RecordingResult>((resolve, reject) => {
        stopResolve = resolve;
        stopReject = reject;
      });
      if (recorder.state === 'recording') {
        try {
          recorder.stop();
        } catch (err) {
          if (stopReject) {
            stopReject(err as Error);
            stopReject = null;
          }
        }
      }
      return stopPromise;
    },
    cancel() {
      if (internalState === 'stopped' || internalState === 'error') return;
      if (stopHandle !== undefined) clearTimeoutFn(stopHandle);
      // Tell the onstop handler to skip emitting a result. The handler still
      // runs (cleanup + state emit) but doesn't fill cachedResult.
      suppressResult = true;
      if (recorder.state === 'recording') {
        try {
          recorder.stop();
        } catch {
          // Recorder already inactive; emit cleanup directly.
          stopAllTracks();
          emit('stopped', { autoStopped: false });
        }
      } else {
        stopAllTracks();
        emit('stopped', { autoStopped: false });
      }
    },
    onStateChange(cb) {
      stateListeners.add(cb);
      return () => stateListeners.delete(cb);
    },
  };

  return controller;
}

function filenameFor(codec: RecordingCodec, startedAt: number): string {
  const ext = codec.startsWith('video/webm') ? 'webm' : 'mp4';
  // ISO-ish timestamp without colons (filesystem-safe).
  const stamp = new Date(startedAt).toISOString().replace(/[:.]/g, '-');
  return `recording-${stamp}.${ext}`;
}

function contentTypeFor(codec: RecordingCodec): 'video/webm' | 'video/mp4' {
  // Strip codec params off — `video/webm; codecs=vp8` → `video/webm`.
  return codec.startsWith('video/webm') ? 'video/webm' : 'video/mp4';
}

// ============================================================================
// #297 — Camera-overlay recording (screen + webcam composited via canvas)
// ============================================================================

/**
 * Camera-recording is a strict superset of screen-recording: it needs the
 * same display-capture + MediaRecorder + codec support, PLUS getUserMedia
 * with video constraints. Returns `supported: false` with a specific reason
 * for each missing capability so the widget UI can hide the camera-record
 * button independently of the screen-record button.
 */
export interface CameraCapabilityCheckResult {
  supported: boolean;
  reason?:
    | 'no_get_display_media'
    | 'no_media_recorder'
    | 'no_supported_codec'
    | 'no_get_user_media'
    // #485: getDisplayMedia / getUserMedia exist but a host Permissions-Policy
    // blocks display-capture or camera, so capture would reject on click.
    | 'display_capture_blocked'
    | 'camera_blocked';
  codec?: RecordingCodec;
}

export function checkCameraRecordingSupport(
  options: { isTypeSupported?: (type: string) => boolean; ignorePermissionsPolicy?: boolean } = {},
): CameraCapabilityCheckResult {
  // Reuse the existing screen-record support check so we never permit
  // camera-recording on a browser that can't also screen-record. Same codec
  // discovery + the same getDisplayMedia + MediaRecorder gating.
  const screen = checkRecordingSupport(options);
  if (!screen.supported) {
    return { supported: false, reason: screen.reason };
  }
  // Additional camera-specific gate: getUserMedia must exist. We can't
  // probe permissions without prompting, but presence of the API is a
  // necessary precondition — sandboxed iframes without allow="camera"
  // commonly omit it entirely.
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    return { supported: false, reason: 'no_get_user_media' };
  }
  // #485: getUserMedia exists but a host Permissions-Policy `camera=()` (or an
  // iframe without allow="camera") would make it reject on click. Detect up
  // front so the camera-record button hides instead of throwing reactively.
  if (!options.ignorePermissionsPolicy && !permissionsPolicyAllows('camera')) {
    return { supported: false, reason: 'camera_blocked' };
  }
  return { supported: true, codec: screen.codec };
}

export interface CameraRecordingResult extends RecordingResult {
  /** True when the camera track was successfully attached. False on user-deny fallback. */
  cameraCaptured: boolean;
}

/**
 * Canvas composition geometry. Camera tile sits in the bottom-right at
 * 20% of the canvas height with a 16:9 aspect (matches typical webcam),
 * 12px margin from the edges.
 */
const CAMERA_TILE_HEIGHT_FRACTION = 0.2;
const CAMERA_TILE_ASPECT = 16 / 9;
const CAMERA_TILE_MARGIN_PX = 12;
const CAMERA_DRAW_FPS = 30;

export interface StartCameraRecordingOptions extends StartRecordingOptions {
  /**
   * Optional override for the camera tile's height as a fraction of the
   * captured display height (0 < x ≤ 1). Default 0.2 (20%).
   */
  cameraHeightFraction?: number;
  /**
   * Pluggable for tests. Defaults to document.createElement bound to the
   * widget host document. The composition needs a canvas + two hidden
   * video elements which the test runner doesn't provide by default.
   */
  createElement?: <K extends keyof HTMLElementTagNameMap>(tag: K) => HTMLElementTagNameMap[K];
  /**
   * Pluggable for tests. Defaults to window.requestAnimationFrame. Tests
   * pass a synchronous tick driver so the draw loop is observable.
   */
  requestAnimationFrame?: (cb: FrameRequestCallback) => number;
  /** Pluggable for tests. Defaults to window.cancelAnimationFrame. */
  cancelAnimationFrame?: (handle: number) => void;
}

/**
 * Start a screen-plus-camera recording. Composites the screen capture
 * (full-canvas) with the camera feed (bottom-right tile) into a single
 * canvas-captured stream that MediaRecorder records. Audio from the camera
 * stream is added to the composited stream so the receiver gets one blob
 * with synchronized video + audio.
 *
 * Camera denial fallback: if the user accepts the screen-pick but denies
 * camera, we proceed with screen-only output AND surface `cameraCaptured:
 * false` on the result so the caller can render a "camera unavailable"
 * notice. Better UX than aborting — the user's intent was "record with
 * camera" but partial fulfillment still delivers value.
 *
 * Cleanup contract: every termination path (manual stop, auto-stop,
 * cancel, error, browser stop-sharing) calls `track.stop()` on every
 * track in both source streams + removes the canvas + video elements from
 * the DOM + cancels the rAF loop. Without this the browser's "site is
 * using your camera" indicator stays on indefinitely — privacy + trust
 * regression.
 */
export async function startCameraRecording(
  options: StartCameraRecordingOptions = {},
): Promise<CameraRecordingController> {
  const isTypeSupported =
    options.isTypeSupported ?? MediaRecorder.isTypeSupported.bind(MediaRecorder);
  const support = checkCameraRecordingSupport({ isTypeSupported });
  if (!support.supported || !support.codec) {
    throw new Error(`camera_recording_unsupported:${support.reason ?? 'unknown'}`);
  }

  const maxDurationMs = options.maxDurationMs ?? DEFAULT_MAX_DURATION_MS;
  const maxBitrate = options.maxBitrate ?? DEFAULT_MAX_BITRATE;
  const wantAudio = options.audio !== false;
  const cameraHeightFraction = options.cameraHeightFraction ?? CAMERA_TILE_HEIGHT_FRACTION;

  const getDisplayMedia =
    options.getDisplayMedia ?? ((c) => navigator.mediaDevices.getDisplayMedia(c));
  const getUserMedia = options.getUserMedia ?? ((c) => navigator.mediaDevices.getUserMedia(c));
  const createRecorder =
    options.createRecorder ??
    ((stream, opts) => new MediaRecorder(stream, opts) as unknown as MediaRecorderLike);
  const setTimeoutFn = options.setTimeout ?? ((cb, ms) => setTimeout(cb, ms));
  const clearTimeoutFn = options.clearTimeout ?? ((handle) => clearTimeout(handle as number));
  const now = options.now ?? (() => Date.now());
  const createElement =
    options.createElement ??
    (<K extends keyof HTMLElementTagNameMap>(tag: K) => document.createElement(tag));
  const rAF =
    options.requestAnimationFrame ?? ((cb: FrameRequestCallback) => requestAnimationFrame(cb));
  const cancelAF =
    options.cancelAnimationFrame ?? ((handle: number) => cancelAnimationFrame(handle));

  // ----- 1. Capture display stream -----
  // Display-pick dialog runs here; rejection propagates to caller before we
  // ask for camera permission (avoid double-prompt-for-nothing pattern).
  const displayConstraints: Record<string, unknown> = {
    video: { displaySurface: 'browser' },
    preferCurrentTab: true,
  };
  const displayStream = await getDisplayMedia(displayConstraints as MediaStreamConstraints);

  // ----- 2. Capture camera stream (with graceful denial fallback) -----
  let cameraStream: MediaStream | null = null;
  let cameraCaptured = false;
  try {
    cameraStream = await getUserMedia({
      video: true,
      audio: wantAudio,
    });
    cameraCaptured = true;
  } catch {
    // User denied camera (or no camera available). Fall back to audio-only
    // attempt + screen-only video. Same pattern as the existing audio
    // fallback in startRecording — never abort just because part of the
    // capture pipeline got refused.
    if (wantAudio) {
      try {
        cameraStream = await getUserMedia({ audio: true });
      } catch {
        // No audio either; proceed video-only.
      }
    }
  }

  // ----- 3. Build the composition canvas + video sources -----
  const displayVideoEl = createElement('video');
  displayVideoEl.muted = true;
  displayVideoEl.playsInline = true;
  displayVideoEl.srcObject = displayStream;
  await displayVideoEl.play().catch(() => undefined);

  let cameraVideoEl: HTMLVideoElement | null = null;
  if (cameraCaptured && cameraStream && cameraStream.getVideoTracks().length > 0) {
    cameraVideoEl = createElement('video');
    cameraVideoEl.muted = true;
    cameraVideoEl.playsInline = true;
    // Build a video-only stream for the canvas paint — audio joins the
    // composited stream separately so the recorded blob still carries it.
    cameraVideoEl.srcObject = new MediaStream(cameraStream.getVideoTracks());
    await cameraVideoEl.play().catch(() => undefined);
  }

  // Canvas dimensions follow the display stream's intrinsic size when
  // available. Fall back to 1280×720 (the size most browsers seed
  // getDisplayMedia with) before metadata arrives.
  const displayTrack = displayStream.getVideoTracks()[0];
  const displaySettings = displayTrack?.getSettings();
  const canvasWidth = displaySettings?.width ?? 1280;
  const canvasHeight = displaySettings?.height ?? 720;

  const canvas = createElement('canvas');
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    // 2D context unavailable → abort cleanly + stop allocated streams.
    for (const t of displayStream.getTracks()) {
      try {
        t.stop();
      } catch {
        // best-effort
      }
    }
    if (cameraStream) {
      for (const t of cameraStream.getTracks()) {
        try {
          t.stop();
        } catch {
          // best-effort
        }
      }
    }
    throw new Error('camera_recording_canvas_unavailable');
  }

  // ----- 4. Compute camera tile rect (recomputed each frame in case the
  //          camera intrinsic ratio differs from 16:9) -----
  function cameraTileRect(): { x: number; y: number; w: number; h: number } {
    const h = canvasHeight * cameraHeightFraction;
    const aspect =
      cameraVideoEl?.videoWidth && cameraVideoEl.videoHeight
        ? cameraVideoEl.videoWidth / cameraVideoEl.videoHeight
        : CAMERA_TILE_ASPECT;
    const w = h * aspect;
    const x = canvasWidth - w - CAMERA_TILE_MARGIN_PX;
    const y = canvasHeight - h - CAMERA_TILE_MARGIN_PX;
    return { x, y, w, h };
  }

  // ----- 5. Draw loop — rAF-driven so it pauses cleanly when the tab is
  //          backgrounded + costs nothing extra over MediaRecorder's own
  //          frame cadence. -----
  let rafHandle: number | null = null;
  let drawing = true;
  function drawOnce(): void {
    if (!drawing) return;
    try {
      ctx?.drawImage(displayVideoEl, 0, 0, canvasWidth, canvasHeight);
      if (cameraVideoEl && cameraVideoEl.videoWidth > 0) {
        const r = cameraTileRect();
        // Translucent dark backing so the tile reads against a light screen.
        ctx?.fillRect(r.x - 1, r.y - 1, r.w + 2, r.h + 2);
        ctx?.drawImage(cameraVideoEl, r.x, r.y, r.w, r.h);
      }
    } catch {
      // drawImage can throw if a source video element loses its source
      // mid-frame. Swallow to keep the loop alive; the recorder still
      // gets the last good frame from the canvas captureStream.
    }
    rafHandle = rAF(drawOnce);
  }
  rafHandle = rAF(drawOnce);

  // ----- 6. Capture the composited canvas as a stream + add audio -----
  const compositedStream = canvas.captureStream(CAMERA_DRAW_FPS);
  if (cameraStream) {
    for (const audioTrack of cameraStream.getAudioTracks()) {
      compositedStream.addTrack(audioTrack);
    }
  }

  const codec = support.codec;
  const recorder = createRecorder(compositedStream, {
    mimeType: codec,
    videoBitsPerSecond: maxBitrate,
  });

  const chunks: Blob[] = [];
  const startedAt = now();
  let stopHandle: unknown;
  let internalState: RecordingState = 'recording';
  let autoStopped = false;
  let stopPromise: Promise<CameraRecordingResult> | null = null;
  let stopResolve: ((r: CameraRecordingResult) => void) | null = null;
  let stopReject: ((e: Error) => void) | null = null;
  let cachedResult: CameraRecordingResult | null = null;
  let cachedError: Error | null = null;
  let suppressResult = false;
  const stateListeners = new Set<(s: RecordingState, d?: StateChangeDetail) => void>();

  function emit(state: RecordingState, detail?: StateChangeDetail): void {
    internalState = state;
    for (const cb of stateListeners) {
      try {
        cb(state, detail);
      } catch {
        // Listener errors must not break the recording lifecycle.
      }
    }
  }

  function teardown(): void {
    drawing = false;
    if (rafHandle !== null) {
      cancelAF(rafHandle);
      rafHandle = null;
    }
    for (const track of displayStream.getTracks()) {
      try {
        track.stop();
      } catch {
        // best-effort
      }
    }
    if (cameraStream) {
      for (const track of cameraStream.getTracks()) {
        try {
          track.stop();
        } catch {
          // best-effort
        }
      }
    }
    // Detach srcObject so the browser releases the underlying tracks
    // promptly even if a DOM ref to the video elements lingers.
    try {
      displayVideoEl.srcObject = null;
    } catch {
      // best-effort
    }
    if (cameraVideoEl) {
      try {
        cameraVideoEl.srcObject = null;
      } catch {
        // best-effort
      }
    }
  }

  recorder.ondataavailable = (event) => {
    if (event.data && event.data.size > 0) chunks.push(event.data);
  };

  recorder.onstop = () => {
    if (stopHandle !== undefined) clearTimeoutFn(stopHandle);
    teardown();
    const durationMs = now() - startedAt;
    if (suppressResult) {
      emit('stopped', { autoStopped: false });
      return;
    }
    const blob = new Blob(chunks, { type: codec });
    const result: CameraRecordingResult = {
      blob,
      filename: filenameFor(codec, startedAt),
      contentType: contentTypeFor(codec),
      durationMs,
      autoStopped,
      audioCaptured: !!cameraStream && cameraStream.getAudioTracks().length > 0,
      cameraCaptured,
    };
    cachedResult = result;
    emit('stopped', { durationMs, autoStopped });
    if (stopResolve) {
      stopResolve(result);
      stopResolve = null;
    }
  };

  recorder.onerror = (event) => {
    if (stopHandle !== undefined) clearTimeoutFn(stopHandle);
    teardown();
    const error = event.error ?? new Error('recorder_error');
    cachedError = error;
    emit('error', { error: error.message });
    if (stopReject) {
      stopReject(error);
      stopReject = null;
    }
  };

  // Browsers fire 'ended' on the display track when the user clicks the
  // browser's "Stop sharing" toolbar button. Treat that as manual stop
  // (same as the screen-only recorder).
  for (const track of displayStream.getVideoTracks()) {
    track.addEventListener('ended', () => {
      if (recorder.state === 'recording') {
        try {
          recorder.stop();
        } catch {
          // already stopped
        }
      }
    });
  }

  recorder.start(1000);
  emit('recording');

  stopHandle = setTimeoutFn(() => {
    if (recorder.state === 'recording') {
      autoStopped = true;
      try {
        recorder.stop();
      } catch {
        // already stopped
      }
    }
  }, maxDurationMs);

  const controller: CameraRecordingController = {
    get state() {
      return internalState;
    },
    get stopped() {
      return internalState === 'stopped' || internalState === 'error';
    },
    stop() {
      if (stopPromise) return stopPromise;
      if (cachedResult) return Promise.resolve(cachedResult);
      if (cachedError) return Promise.reject(cachedError);
      stopPromise = new Promise<CameraRecordingResult>((resolve, reject) => {
        stopResolve = resolve;
        stopReject = reject;
      });
      if (recorder.state === 'recording') {
        try {
          recorder.stop();
        } catch (err) {
          if (stopReject) {
            stopReject(err as Error);
            stopReject = null;
          }
        }
      }
      return stopPromise;
    },
    cancel() {
      if (internalState === 'stopped' || internalState === 'error') return;
      if (stopHandle !== undefined) clearTimeoutFn(stopHandle);
      suppressResult = true;
      if (recorder.state === 'recording') {
        try {
          recorder.stop();
        } catch {
          teardown();
          emit('stopped', { autoStopped: false });
        }
      } else {
        teardown();
        emit('stopped', { autoStopped: false });
      }
    },
    onStateChange(cb) {
      stateListeners.add(cb);
      return () => stateListeners.delete(cb);
    },
  };

  return controller;
}

export interface CameraRecordingController {
  readonly state: RecordingState;
  readonly stopped: boolean;
  stop(): Promise<CameraRecordingResult>;
  cancel(): void;
  onStateChange(cb: (state: RecordingState, detail?: StateChangeDetail) => void): () => void;
}
